import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { BetaReaderShell } from '../components/BetaReaderShell';
import { ReaderRoutesProvider } from '../providers/ReaderRoutesProvider';
import { listBetaSearches } from '../repositories/betaSearchRepository';
import { feedPagePath } from '../utils/feedLinks';
import { isCurrentFeed } from '../utils/feedSwitcher';
import { homeLinkTarget, V2_READER_ROUTES } from '../utils/readerRoutes';
import FeedV2Page from './FeedV2Page';
import ForYouV2Page from './ForYouV2Page';
import ReaderFeedsPage from './ReaderFeedsPage';
import SearchV2Page from './SearchV2Page';

/**
 * The reader's entry routes: the feeds v2 views for everyone. Signed in, each
 * load builds its feeds; signed out, the pages show the feeds' stored builds,
 * which the public feed builder keeps fresh for the public feeds (a build
 * spends decision-model calls and takes an account). A signed-out search
 * builds, admitted per visitor.
 */

function V2({ children }: { children: React.ReactNode }) {
  return <ReaderRoutesProvider routes={V2_READER_ROUTES}>{children}</ReaderRoutesProvider>;
}

/** `/`: For You. Old links whose query string asks for another view go there (`homeLinkTarget`). */
export function ReaderHomeRoute() {
  const location = useLocation();
  const target = homeLinkTarget(location.search);
  const hours72 = new URLSearchParams(location.search).get('hours') === '72';
  if (target && 'to' in target) return <Navigate to={target.to} replace />;
  if (target) return <InterestRedirect interestId={target.interestId} hours72={hours72} />;
  return <V2><ForYouV2Page /></V2>;
}

/** `/feeds`: the Feeds page. */
export function ReaderFeedsRoute() {
  return <V2><ReaderFeedsPage /></V2>;
}

/** `/feeds/:feedId`: one feed. */
export function ReaderFeedRoute() {
  return <V2><FeedV2Page /></V2>;
}

/** `/search/<id>` and `/search?q=`: a feeds v2 search. */
export function ReaderSearchRoute() {
  return <V2><SearchV2Page /></V2>;
}

/**
 * An old `/?interest=<id>` link: that feed's page. The id may be a feed's slug or one of its legacy ids
 * (an imported subscription keeps the ids it had), so it is resolved through the catalog, as the feed
 * switcher resolves the current feed; an id the catalog doesn't know opens as given.
 */
function InterestRedirect({ interestId, hours72 }: { interestId: string; hours72: boolean }) {
  const navigate = useNavigate();
  useEffect(() => {
    let disposed = false;
    const open = (feedId: string) => {
      if (!disposed) navigate(`${feedPagePath(feedId)}${hours72 ? '?hours=72' : ''}`, { replace: true });
    };
    listBetaSearches()
      .then(catalog => open([...catalog.searches, ...catalog.public_searches].find(feed => isCurrentFeed(feed, interestId))?.id ?? interestId))
      .catch(() => open(interestId));
    return () => { disposed = true; };
  }, [interestId, hours72, navigate]);
  return <BetaReaderShell><p role="status">Opening your feed…</p></BetaReaderShell>;
}
